/**
 * WWDカルーセル制御
 * GSAP ScrollTriggerを使用したスクロール連動実装
 */
class WWDCarousel {
  constructor(container, options = {}) {
    this.container = container;
    this.track = container.querySelector('.wwd__carousel-track');
    this.slides = container.querySelectorAll('.wwd__carousel-slide');
    this.isRight = container.classList.contains('wwd__carousel-right');
    this.isLeft = container.classList.contains('wwd__carousel-left');
    this.scrollTrigger = null;
    this.timeline = null;
    this.currentX = null;
    this.followTween = null;
    this.maxDeltaPx = 80;
    
    // イージング設定
    this.easingType = options.easingType || 'smooth'; // デフォルトは滑らかなイージング
    this.animationDuration = options.animationDuration || 0.3; // 慣性を感じる追従時間
    
    this.init();
  }
  
  async init() {
    if (!this.track || !this.slides.length) {
      return;
    }
    
    // GSAPが利用可能かチェック
    if (typeof gsap === 'undefined') {
      return;
    }
    
    // ScrollTriggerプラグインを登録
    if (typeof ScrollTrigger === 'undefined') {
      return;
    }
    
    gsap.registerPlugin(ScrollTrigger);
    
    this.duplicateSlides();
    
    // 画像の読み込みを待つ
    await this.waitForImages();
    
    this.setupScrollTrigger();
    this.setupReducedMotion();
    
  }
  
  /**
   * 画像の読み込み完了を待つ
   */
  waitForImages(timeout = 4000) {
    return new Promise((resolve) => {
      const images = Array.from(this.slides)
        .map(slide => slide.querySelector('img'))
        .filter(Boolean);
      let loadedCount = 0;
      let resolved = false;
      
      if (images.length === 0) {
        resolve();
        return;
      }
      
      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      
      const timeoutId = window.setTimeout(() => {
        finish();
      }, timeout);
      
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === images.length) {
          window.clearTimeout(timeoutId);
          finish();
        }
      };
      
      images.forEach(img => {
        if (!img) {
          checkComplete();
          return;
        }
        
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete, { once: true });
          img.addEventListener('error', checkComplete, { once: true });
        }
      });
    });
  }
  
  /**
   * スライドを複製して無限ループを実現
   */
  duplicateSlides() {
    // 既に複製されている場合はスキップ
    if (this.track.children.length > this.slides.length) {
      return;
    }
    
    // 元のスライドを複製
    const originalSlides = Array.from(this.slides);
    originalSlides.forEach(slide => {
      const clone = slide.cloneNode(true);
      this.track.appendChild(clone);
    });
    
  }
  
  /**
   * カスタムイージング関数
   */
  createCustomEase() {
    // 複数のイージングオプションから選択
    const easingOptions = {
      smooth: "power2.out",      // 滑らかで自然
      gentle: "power1.out",      // より穏やか
      linear: "none",            // 線形（一定速度）
      elastic: "elastic.out(1, 0.3)", // 弾性のある動き
      bounce: "bounce.out",      // バウンス効果
      back: "back.out(1.7)"      // オーバーシュート効果
    };
    
    // 設定されたイージングタイプを使用（デフォルトは滑らかなイージング）
    return gsap.parseEase(easingOptions[this.easingType] || easingOptions.smooth);
  }
  
  /**
   * GSAP ScrollTriggerを設定してスクロール連動
   */
  setupScrollTrigger() {
    // カルーセルの幅を計算
    const trackWidth = this.track.scrollWidth;
    const containerWidth = this.container.offsetWidth;
    const totalDistance = trackWidth - containerWidth;
    
    // Lenisが有効な場合はmovementScaleを小さくして移動距離を制限
    const movementScale = (window.lenis && window.lenis.isInitialized) 
      ? 0.4  // Lenis使用時は40%に縮小
      : 0.65; // 通常時は65%
    
    const adjustedDistance = totalDistance * movementScale;
    
    // Lenisが有効な場合はmaxSpeedPxを小さくする
    const maxSpeedPx = (window.lenis && window.lenis.isInitialized)
      ? 30  // Lenis使用時は30pxに制限
      : 100; // 通常時は100px
    
    this.maxDeltaPx = Math.min(maxSpeedPx, Math.max(40, adjustedDistance * 0.08));
    
    
    // 移動距離が0以下の場合は処理をスキップ
    if (adjustedDistance <= 0) {
      return;
    }
    
    // 移動方向を決定
    const xFrom = this.isRight ? 0 : -adjustedDistance;
    const xTo = this.isRight ? -adjustedDistance : 0;
    
    // カスタムイージング関数を作成
    const customEase = this.createCustomEase();
    
    // 初期位置を設定
    gsap.set(this.track, { x: xFrom });
    
    // Lenisが有効な場合はscrubの値を大きくして追従を遅くする
    // Lenisのスムーズスクロールと組み合わさると、scrubが小さすぎると追従が速くなりすぎる
    const scrubValue = (window.lenis && window.lenis.isInitialized) ? 3.0 : 0.3;
    
    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.container,
      start: "top bottom",
      end: "bottom top",
      scrub: scrubValue,
      onUpdate: (self) => {
        const easedProgress = customEase(self.progress);
        const targetX = gsap.utils.interpolate(xFrom, xTo, easedProgress);
        this.updatePosition(targetX);
      },
    });
    
  }
  
  /**
   * スクロール進行度に応じたアニメーション更新
   */
  updateAnimation(progress) {
    const trackWidth = this.track.scrollWidth;
    const containerWidth = this.container.offsetWidth;
    const totalDistance = trackWidth - containerWidth;
    const movementScale = 0.65;
    const adjustedDistance = totalDistance * movementScale;
    
    const xFrom = this.isRight ? 0 : -adjustedDistance;
    const xTo = this.isRight ? -adjustedDistance : 0;
    const targetX = gsap.utils.interpolate(xFrom, xTo, progress);
    
    this.updatePosition(targetX, true);
  }
  
  /**
   * prefers-reduced-motion対応
   */
  setupReducedMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (this.timeline) {
        this.timeline.kill();
      }
    }
  }
  
  /**
   * リソースのクリーンアップ
   */
  destroy() {
    if (this.followTween) {
      this.followTween.kill();
      this.followTween = null;
    }
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
      this.scrollTrigger = null;
    }
    this.timeline = null;
  }
  
  /**
   * 実際の移動処理
   */
  updatePosition(targetX, immediate = false) {
    if (this.currentX === null || immediate) {
      this.currentX = targetX;
      gsap.set(this.track, { x: this.currentX });
      return;
    }
    
    // Lenisが有効な場合はmaxDeltaPxを小さくして、より滑らかな追従にする
    const maxDelta = (window.lenis && window.lenis.isInitialized) 
      ? this.maxDeltaPx * 0.2  // Lenis使用時は20%に（さらに遅く）
      : this.maxDeltaPx;
    
    const delta = targetX - this.currentX;
    const clampedDelta = gsap.utils.clamp(-maxDelta, maxDelta, delta);
    this.currentX += clampedDelta;
    
    if (this.followTween) {
      this.followTween.kill();
    }
    
    // Lenisが有効な場合はdurationを長くして、より滑らかな追従にする
    const duration = (window.lenis && window.lenis.isInitialized)
      ? this.animationDuration * 2.5  // さらに長くして遅くする
      : this.animationDuration;
    
    this.followTween = gsap.to(this.track, {
      x: this.currentX,
      duration: duration,
      ease: "power2.out",
      overwrite: true
    });
  }
}

/**
 * 初期化処理
 */
async function initWWDCarousels() {
  // GSAPとScrollTriggerが読み込まれるまで待機
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    setTimeout(initWWDCarousels, 100);
    return;
  }
  
  const carouselContainers = document.querySelectorAll('.wwd__carousel-right, .wwd__carousel-left');
  
  if (carouselContainers.length === 0) {
    return;
  }
  
  // 既存のインスタンスがあれば破棄
  if (window.__wwdCarousels && Array.isArray(window.__wwdCarousels)) {
    window.__wwdCarousels.forEach((carousel) => {
      if (carousel && typeof carousel.destroy === 'function') {
        carousel.destroy();
      }
    });
  }
  
  const carousels = Array.from(carouselContainers).map((container) => new WWDCarousel(container));
  
  
  window.__wwdCarousels = carousels;
}

// DOM読み込み完了後に初期化
document.addEventListener('DOMContentLoaded', initWWDCarousels);

// メインコンテンツ表示後にも初期化（ローディング画面対応）
window.addEventListener('app:main-shown', initWWDCarousels);

export { WWDCarousel, initWWDCarousels };
